export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MOBILE_IMAGE_TARGET_BYTES = 2 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 2560;
const JPEG_HEADER_BYTES = 256 * 1024;

type ExifOrientation = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

type JpegMetadata = {
  orientation: ExifOrientation;
};

const IMAGE_MIME_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp',
  gif: 'image/gif', heic: 'image/heic', heif: 'image/heif', bmp: 'image/bmp',
  tif: 'image/tiff', tiff: 'image/tiff',
};

function extension(name: string): string {
  return String(name || '').trim().toLowerCase().split('.').pop() || '';
}

async function detectedImageMimeType(file: File): Promise<string> {
  const declared = String(file.type || '').trim().toLowerCase();
  if (declared.startsWith('image/')) return declared;
  const fromName = IMAGE_MIME_BY_EXTENSION[extension(file.name)];
  if (fromName) return fromName;

  const bytes = new Uint8Array(await file.slice(0, 32).arrayBuffer());
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'image/png';
  const header = String.fromCharCode(...bytes);
  if (header.startsWith('GIF8')) return 'image/gif';
  if (header.startsWith('RIFF') && header.slice(8, 12) === 'WEBP') return 'image/webp';
  if (header.slice(4, 8) === 'ftyp') {
    const brand = header.slice(8, 12).toLowerCase();
    if (['heic', 'heix', 'hevc', 'hevx'].includes(brand)) return 'image/heic';
    if (['heif', 'mif1', 'msf1'].includes(brand)) return 'image/heif';
  }
  return '';
}

async function normalizedImageFile(file: File): Promise<File> {
  const mimeType = await detectedImageMimeType(file);
  if (!mimeType) throw new Error(`${file.name || 'Fișierul selectat'} nu este o imagine validă.`);
  if (file.type === mimeType) return file;
  return new File([file], file.name || `fotografie.${mimeType === 'image/png' ? 'png' : 'jpg'}`, {
    type: mimeType,
    lastModified: file.lastModified || Date.now(),
  });
}

function exifOrientation(view: DataView, start: number, length: number): ExifOrientation | undefined {
  if (length < 14
    || view.getUint32(start, false) !== 0x45786966
    || view.getUint16(start + 4, false) !== 0) return undefined;

  const tiff = start + 6;
  const byteOrder = view.getUint16(tiff, false);
  const littleEndian = byteOrder === 0x4949;
  if (!littleEndian && byteOrder !== 0x4d4d) return undefined;

  try {
    if (view.getUint16(tiff + 2, littleEndian) !== 42) return undefined;
    const directory = tiff + view.getUint32(tiff + 4, littleEndian);
    const entries = view.getUint16(directory, littleEndian);
    for (let index = 0; index < entries; index += 1) {
      const entry = directory + 2 + (index * 12);
      if (entry + 12 > start + length || view.getUint16(entry, littleEndian) !== 0x0112) continue;
      const value = view.getUint16(entry + 8, littleEndian);
      return value >= 1 && value <= 8 ? value as ExifOrientation : undefined;
    }
  } catch {
    // Metadatele EXIF trunchiate sau invalide nu trebuie să blocheze fotografia.
  }
  return undefined;
}

function jpegMetadata(buffer: ArrayBuffer): JpegMetadata {
  const view = new DataView(buffer);
  const result: JpegMetadata = { orientation: 1 };
  if (view.byteLength < 4 || view.getUint16(0, false) !== 0xffd8) return result;

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    while (offset < view.byteLength && view.getUint8(offset) === 0xff) offset += 1;
    if (offset >= view.byteLength) break;
    const marker = view.getUint8(offset); offset += 1;
    if (marker === 0xd9 || marker === 0xda) break;
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;
    if (offset + 2 > view.byteLength) break;
    const segmentLength = view.getUint16(offset, false);
    if (segmentLength < 2 || offset + segmentLength > view.byteLength) break;
    const segmentStart = offset + 2;
    const payloadLength = segmentLength - 2;

    if (marker === 0xe1) result.orientation = exifOrientation(view, segmentStart, payloadLength) || result.orientation;
    offset += segmentLength;
  }
  return result;
}

async function imageMetadata(file: File): Promise<JpegMetadata> {
  if (file.type !== 'image/jpeg') return { orientation: 1 };
  return jpegMetadata(await file.slice(0, JPEG_HEADER_BYTES).arrayBuffer());
}

type LoadedImage = {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
};

async function loadImage(file: File): Promise<LoadedImage> {
  const url = URL.createObjectURL(file);
  return new Promise<LoadedImage>((resolve, reject) => {
    const image = new Image();
    // `from-image` este comportamentul implicit modern și include toate cele opt
    // transformări EXIF. Folosim intenționat <img>, deoarece implementările mobile
    // ale createImageBitmap tratează inconsistent opțiunea imageOrientation.
    image.style.imageOrientation = 'from-image';
    image.onload = () => resolve({
      source: image,
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
      release: () => URL.revokeObjectURL(url),
    });
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Imaginea nu poate fi procesată de acest browser.')); };
    image.src = url;
  });
}

function canvasBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Imaginea nu a putut fi comprimată.')), 'image/jpeg', quality));
}

function jpegName(name: string): string {
  const base = name.replace(/\.[^.]+$/, '').trim() || 'fotografie';
  return `${base}.jpg`;
}

export async function fitImageForUpload(file: File, maxBytes = MOBILE_IMAGE_TARGET_BYTES): Promise<File> {
  const normalized = await normalizedImageFile(file);
  const metadata = await imageMetadata(normalized);
  // Un JPEG cu EXIF diferit de 1 trebuie trecut prin canvas chiar dacă este mic:
  // pixelii sunt salvați fizic în orientarea corectă, iar EXIF-ul nu mai este necesar.
  if (normalized.size <= maxBytes && metadata.orientation === 1) return normalized;

  let loaded: LoadedImage;
  try {
    loaded = await loadImage(normalized);
  } catch (error) {
    // Safari poate furniza HEIC/HEIF valid, dar fără suport de decodare în canvas.
    // Dacă fișierul încape în limita serverului îl păstrăm în formatul original.
    if (normalized.size <= MAX_IMAGE_BYTES) return normalized;
    throw error;
  }
  try {
    const sourceWidth = Math.max(1, loaded.width);
    const sourceHeight = Math.max(1, loaded.height);
    const initialScale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(sourceWidth, sourceHeight), Math.sqrt((maxBytes * 0.82) / normalized.size));
    let width = Math.max(1, Math.round(sourceWidth * initialScale));
    let height = Math.max(1, Math.round(sourceHeight * initialScale));
    let quality = 0.9;

    for (let attempt = 0; attempt < 12; attempt += 1) {
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Browserul nu poate redimensiona această imagine.');
      context.fillStyle = '#ffffff'; context.fillRect(0, 0, width, height);
      context.drawImage(loaded.source, 0, 0, width, height);
      const blob = await canvasBlob(canvas, quality);
      if (blob.size <= maxBytes) return new File([blob], jpegName(normalized.name), { type: 'image/jpeg', lastModified: Date.now() });
      if (quality > 0.55) quality -= 0.1;
      else { width = Math.max(1, Math.round(width * 0.82)); height = Math.max(1, Math.round(height * 0.82)); quality = 0.82; }
    }
    const targetMb = Math.max(1, Math.floor(maxBytes / (1024 * 1024)));
    throw new Error(`Imaginea ${normalized.name} nu a putut fi redusă sub ${targetMb} MB.`);
  } finally {
    loaded.release();
  }
}

export async function prepareImageFiles(files: File[], maxBytes = MOBILE_IMAGE_TARGET_BYTES): Promise<File[]> {
  const prepared: File[] = [];
  // Procesarea secvențială evită vârfurile de memorie pe iPhone când sunt selectate
  // mai multe fotografii de rezoluție mare din galerie.
  for (const file of files) prepared.push(await fitImageForUpload(file, maxBytes));
  return prepared;
}
